import { useCallback, useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { Server } from "@modules/api";
import {
  getQueryFromUrl,
  updateUrlQueryParams,
  updateQueryParam,
  withRedirectUrl,
} from "@utils/helpers/getQueryFromUrl";
import {
  checkB2BLoginOption,
  clearCheckB2BLoginOption,
  requestPasswordReset,
  signInWithB2B,
  useCheckB2BLoginOption,
  useSignInWithB2B,
} from "@lib/stores";
import { onLogin } from "@utils/helpers/nativeFunctions";
import { useAppNavigate } from "@utils/hooks/useAppNavigate";

type InputsEmail = {
  email: string;
};

type Inputs = {
  email: string;
  password: string;
};

export const useB2BLogin = (language: string) => {
  const navigate = useAppNavigate();

  const emailSearchParam = getQueryFromUrl("email");

  const [email, setEmail] = useState<string | undefined>(undefined);
  const [password, setPassword] = useState<string | undefined>(undefined);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [isPasswordExpired, setIsPasswordExpired] = useState(false);

  const { data: b2bLogin, status } = useSignInWithB2B();
  const checkLoginOption = useCheckB2BLoginOption();

  const {
    register: registerEmail,
    handleSubmit: handleSubmitEmail,
    control: controlEmail,
    watch: watchEmail,
    setValue: setValueEmail,
    formState: formStateEmail,
    trigger: triggerEmail,
  } = useForm<InputsEmail>({ mode: "all", defaultValues: { email: "" } });

  const { register, handleSubmit, control, reset, watch, formState } =
    useForm<Inputs>({
      mode: "all",
      defaultValues: { email: "", password: "" },
    });

  watchEmail((data) => {
    setEmail(data.email);
  });

  watch((data) => {
    setPassword(data.password);
  });

  const onCancel = useCallback(() => {
    setShowPasswordForm(false);
  }, []);

  const onCreatePasswordFormCancel = useCallback(() => {
    setIsPasswordExpired(false);
  }, []);

  const resendPasswordCode = useCallback(
    async (create: boolean) => {
      const pathname = window.location.pathname;

      //TODO
      //success message
      const successMessage = pathname.includes("/en/")
        ? "The code has been sent to your email"
        : "تم إرسال الرمز إلى بريدك الإلكتروني";

      requestPasswordReset({ email: emailSearchParam || "" }, () => {
        navigate("/login/b2b/reset-password/verify", {
          create: create ? "1" : "0",
          email: encodeURIComponent(emailSearchParam || ""),
        });
      });
    },
    [emailSearchParam, navigate]
  );

  const onSubmitEmail: SubmitHandler<InputsEmail> = (data) => {
    updateQueryParam("email", data.email);

    checkB2BLoginOption(data);
  };

  const onSubmit: SubmitHandler<Inputs> = (data) => {
    signInWithB2B(data);
  };

  const pathname = window.location.pathname;

  useEffect(() => {
    if (emailSearchParam) {
      setValueEmail("email", emailSearchParam);

      triggerEmail();
    }

    if (!emailSearchParam) {
      clearCheckB2BLoginOption();
    }
  }, [emailSearchParam]);

  useEffect(() => {
    if (email) {
      reset({ email });
    }
  }, [email, reset]);

  useEffect(() => {
    const checkLoginOptionData = checkLoginOption.data;

    if (checkLoginOptionData) {
      if (
        checkLoginOptionData?.loginOption ===
          Server.LoginOption.ExternalProvider &&
        checkLoginOptionData.url
      ) {
        const returnUrl = GetReturnUrl(checkLoginOptionData.url);
        const redirectUrl = getQueryFromUrl("redirectUrl");
        const AICC_SID = getQueryFromUrl("AICC_SID", redirectUrl);
        const AICC_URL = getQueryFromUrl("AICC_URL", redirectUrl);
        console.log(returnUrl);
        const url = updateUrlQueryParams(checkLoginOptionData.url, [
          { key: "returnUrl", value: returnUrl },
          { key: "AICC_SID", value: AICC_SID },
          { key: "AICC_URL", value: AICC_URL },
        ]);
        console.log(url);
        window.location.href = url;
      } else if (
        checkLoginOptionData?.loginOption === Server.LoginOption.EmailPassword
      ) {
        setShowPasswordForm(true);
      } else if (
        checkLoginOptionData?.loginOption === Server.LoginOption.CreatePassword
      ) {
        updateQueryParam("create", "1");
        updateQueryParam("email", emailSearchParam);

        resendPasswordCode(true);
      }
    } else {
      setShowPasswordForm(false);
    }
  }, [checkLoginOption, navigate, emailSearchParam, resendPasswordCode]);

  useEffect(() => {
    if (b2bLogin) {
      if (!b2bLogin.passwordExpired) {
        const languageId = language === "ar" ? 1 : 2;

        const data = {
          ...b2bLogin,
          languageId,
        };

        onLogin(JSON.stringify(data));
      } else {
        setIsPasswordExpired(true);
      }
    }
  }, [b2bLogin, language]);

  //   useEffect(() => {
  //     const searchParams = {
  //       emailSearchParam: emailSearchParam || email,
  //     };

  //     commonDispatch(
  //       CommonActions.setSearchParams({
  //         search: {
  //           pathname,
  //           search: { ...searchParams },
  //         },
  //       })
  //     );

  //     commonDispatch(
  //       CommonActions.addBackNavigationItem({
  //         pageName: 'b2bLogin',
  //         pathname,
  //         search: { ...searchParams },
  //         forceAdd: true,
  //       })
  //     );
  //   }, [emailSearchParam, commonDispatch, pathname, email]);

  return {
    emailSearchParam,
    showPasswordForm,
    status,
    registerEmail,
    handleSubmitEmail,
    controlEmail,
    register,
    handleSubmit,
    control,
    onSubmitEmail,
    onSubmit,
    checkLoginOption,
    b2bLogin,
    resendPasswordCode,
    isPasswordExpired,
    password,
    formStateEmail,
    formState,
    onCancel,
    onCreatePasswordFormCancel,
  };
};

function GetReturnUrl(url: string) {
  let returnUrl = getQueryFromUrl("returnUrl", url);
  console.log(returnUrl);
  if (returnUrl) {
    returnUrl = withRedirectUrl(returnUrl);
  }
  return returnUrl;
}
